'use client';

import { useState, type FormEvent, type HTMLInputTypeAttribute } from 'react';

import { Button } from '@/components/ui/Button';
import { TextAreaField, TextField } from '@/components/ui/Field';
import { cn } from '@/lib/cn';
import { WARRANTY_SHIPPING_ADDRESS, WARRANTY_SUPPORT_EMAIL } from '@/lib/warranty/content';
import { validateWarrantyClaim, type ClaimType, type PurchaseSource } from '@/lib/warranty/schema';

type FormValues = {
  claimType: ClaimType;
  name: string;
  company: string;
  phone: string;
  email: string;
  serialNumber: string;
  purchaseDate: string;
  purchasedFrom: PurchaseSource | '';
  distributorName: string;
  currentPlatform: string;
  requestedPlatform: string;
  description: string;
  website: string;
};

const EMPTY_FORM: FormValues = {
  claimType: 're-imaging',
  name: '',
  company: '',
  phone: '',
  email: '',
  serialNumber: '',
  purchaseDate: '',
  purchasedFrom: '',
  distributorName: '',
  currentPlatform: '',
  requestedPlatform: '',
  description: '',
  website: '',
};

type TextKey = Exclude<keyof FormValues, 'claimType' | 'purchasedFrom'>;

type FieldConfig = {
  key: TextKey;
  label: string;
  type?: HTMLInputTypeAttribute;
  autoComplete?: string;
  placeholder?: string;
  optional?: boolean;
  hint?: string;
};

const CONTACT_FIELDS: FieldConfig[] = [
  { key: 'name', label: 'Name', autoComplete: 'name' },
  { key: 'company', label: 'Company / location', autoComplete: 'organization', optional: true },
  { key: 'phone', label: 'Phone', type: 'tel', autoComplete: 'tel' },
  { key: 'email', label: 'Email', type: 'email', autoComplete: 'email' },
];

const BOARD_FIELDS: FieldConfig[] = [
  {
    key: 'serialNumber',
    label: 'Computer serial number',
    placeholder: 'As printed on the board label',
    hint: 'Coverage is tracked by serial number — including boards bought from a distributor.',
  },
  { key: 'purchaseDate', label: 'Purchase date (optional)', type: 'date', optional: true },
];

const CLAIM_TYPES: { value: ClaimType; title: string; description: string }[] = [
  { value: 're-imaging', title: 'Re-imaging claim', description: 'Switch the board to another supported platform.' },
  { value: 'repair', title: 'RMA / warranty repair', description: 'The board has a hardware problem.' },
];

type Status = 'idle' | 'submitting' | 'success' | 'error';

export function WarrantyClaimForm() {
  const [values, setValues] = useState<FormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [claimNumber, setClaimNumber] = useState<string | null>(null);

  const isRepair = values.claimType === 'repair';

  const setValue = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage(null);

    const clientErrors = validateWarrantyClaim(values);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    setStatus('submitting');

    try {
      const response = await fetch('/api/warranty-claims/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, pageUrl: window.location.href }),
      });
      const body = (await response.json().catch(() => null)) as {
        ok?: boolean;
        claimNumber?: string;
        message?: string;
        fieldErrors?: Record<string, string>;
      } | null;

      if (response.ok && body?.ok && body.claimNumber) {
        setClaimNumber(body.claimNumber);
        setMessage(body.message ?? 'Your claim has been submitted.');
        setStatus('success');
        return;
      }

      if (body?.fieldErrors) setErrors(body.fieldErrors);
      setMessage(body?.message ?? 'We could not submit your claim. Please try again later.');
      setStatus('error');
    } catch {
      setMessage(`We could not reach the server. Please try again or email ${WARRANTY_SUPPORT_EMAIL}.`);
      setStatus('error');
    }
  };

  if (status === 'success' && claimNumber) {
    return (
      <div className="flex flex-col items-center gap-3 text-center" role="status">
        <h3 className="text-2xl">Claim submitted</h3>
        <p className="text-content-muted text-sm">{message}</p>
        <p className="text-content-subtle mt-2 text-xs font-semibold tracking-[0.2em] uppercase">Your claim number</p>
        <p className="text-brand-bright font-mono text-2xl font-bold break-all" data-testid="claim-number">
          {claimNumber}
        </p>
        <p className="text-content-muted max-w-md text-sm">
          Include your claim number inside the package and mail your computer to {WARRANTY_SHIPPING_ADDRESS.name},{' '}
          {WARRANTY_SHIPPING_ADDRESS.street}, {WARRANTY_SHIPPING_ADDRESS.cityLine}.
        </p>
        <Button
          variant="ghost"
          onClick={() => {
            setValues(EMPTY_FORM);
            setErrors({});
            setStatus('idle');
            setMessage(null);
            setClaimNumber(null);
          }}
        >
          File another claim
        </Button>
      </div>
    );
  }

  const renderField = (field: FieldConfig) => (
    <TextField
      key={field.key}
      label={field.label}
      name={field.key}
      type={field.type ?? 'text'}
      autoComplete={field.autoComplete}
      placeholder={field.placeholder}
      hint={field.hint}
      required={!field.optional}
      error={errors[field.key]}
      value={values[field.key]}
      onChange={(event) => setValue(field.key, event.target.value)}
    />
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate aria-label="Warranty claim">
      <fieldset className="flex flex-col gap-3">
        <legend className="text-content-muted mb-3 text-sm font-medium">What do you need?</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {CLAIM_TYPES.map((type) => {
            const selected = values.claimType === type.value;
            return (
              <button
                key={type.value}
                type="button"
                aria-pressed={selected}
                onClick={() => setValue('claimType', type.value)}
                className={cn(
                  'rounded-2xl border p-4 text-left transition',
                  selected ? 'border-brand bg-brand/10' : 'border-line hover:border-brand/60',
                )}
              >
                <span className="block font-semibold text-white">{type.title}</span>
                <span className="text-content-muted mt-1 block text-sm">{type.description}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-2">{CONTACT_FIELDS.map(renderField)}</div>
      <div className="grid gap-4 sm:grid-cols-2">{BOARD_FIELDS.map(renderField)}</div>

      <fieldset>
        <legend className="text-content-muted text-sm font-medium">Where did you buy the board?</legend>
        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
          {(['direct', 'distributor'] as const).map((source) => (
            <label key={source} className="inline-flex cursor-pointer items-center gap-2 text-sm text-white">
              <input
                type="radio"
                name="purchasedFrom"
                value={source}
                checked={values.purchasedFrom === source}
                onChange={() => setValue('purchasedFrom', source)}
                className="accent-brand h-4 w-4"
              />
              {source === 'direct' ? 'Directly from us' : 'From a distributor'}
            </label>
          ))}
        </div>
        {errors.purchasedFrom ? <p className="text-danger mt-1 text-xs font-medium">{errors.purchasedFrom}</p> : null}
      </fieldset>

      {values.purchasedFrom === 'distributor'
        ? renderField({ key: 'distributorName', label: 'Distributor name', autoComplete: 'off' })
        : null}

      <div className="grid gap-4 sm:grid-cols-2">
        {renderField({ key: 'currentPlatform', label: 'Current platform' })}
        {renderField({ key: 'requestedPlatform', label: 'Requested new platform', optional: isRepair })}
      </div>

      <TextAreaField
        label={isRepair ? 'Describe the problem' : 'Notes (optional)'}
        name="description"
        rows={4}
        required={isRepair}
        error={errors.description}
        value={values.description}
        onChange={(event) => setValue('description', event.target.value)}
      />

      <div className="hidden" aria-hidden="true">
        <label htmlFor="warranty-website">Website</label>
        <input
          id="warranty-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(event) => setValue('website', event.target.value)}
        />
      </div>

      {status === 'error' && message ? (
        <p role="alert" className="border-danger/40 bg-danger/10 text-danger rounded-xl border px-4 py-3 text-sm">
          {message}
        </p>
      ) : null}

      <Button type="submit" disabled={status === 'submitting'} className="sm:self-start">
        {status === 'submitting' ? 'Submitting…' : 'Submit RMA/Warranty Claim'}
      </Button>
    </form>
  );
}
